import { useContext, useEffect, useState } from "react";

import { Container, Grid2Props as GridProps } from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  CopyButton,
  Divider,
  Group,
  Modal,
  Paper,
  Popover,
  Select,
  SelectProps,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  TextProps,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAt,
  IconBook,
  IconCheck,
  IconCreditCard,
  IconEdit,
  IconEye,
  IconKey,
  IconPhone,
  IconPlus,
  IconReload,
  IconShield,
  IconTrash,
  IconUser,
  IconUserCheck,
  IconUsers,
  IconX,
} from "@tabler/icons-react";

import dayjs from "dayjs";
import {
  AdminProfileProps,
  AdminScopeValueProps,
  PaginationProps,
} from "../../Lib/types";

import {
  AdminRolesList,
  AppDomain,
  Colors,
  HttpErrorCodesObj,
  HttpSuccessCodesObj,
} from "../../Lib/data";

import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  showResponseErrors,
  showValidationErrors,
  validateEmail,
} from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import "./styles.scss";

// Extended AdminRolesList to include Sub Admin
const ExtendedAdminRolesList = [...AdminRolesList];

// Scope options with descriptions and icons
const scopeOptions = [
  {
    value: "modify_course" as const,
    label: "Course Management",
    description: "Create, edit, and delete courses and lessons",
    icon: IconBook,
    color: "blue",
  },
  {
    value: "modify_teacher" as const,
    label: "Teacher Management",
    description: "Manage teacher profiles and assignments",
    icon: IconUserCheck,
    color: "green",
  },
  {
    value: "modify_blog" as const,
    label: "Blog Management",
    description: "Create and manage blog posts and content",
    icon: IconEdit,
    color: "violet",
  },
  {
    value: "modify_student" as const,
    label: "Student Management",
    description: "Manage student accounts and enrollments",
    icon: IconUsers,
    color: "orange",
  },
  {
    value: "modify_transaction" as const,
    label: "Transaction Management",
    description: "View and manage payment transactions",
    icon: IconCreditCard,
    color: "teal",
  },
];
const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 4,
    sm: 6,
    xs: 12,
  },
};
const Team: React.FC = () => {
  const context = useContext(AppContext);

  const scopes = context?.scopes ?? [];

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });

  const filterForm = useForm({
    initialValues: {
      role: "all",
    },
    validate: {
      role: (value) =>
        value && value.length > 0 ? null : "Please select a role",
    },
  });
  const {
    data: team,
    reloadData: reloadTeam,
    isLoading: isLoadingTeam,
  } = usePerformRequest<PaginatedResponse<AdminProfileProps[]>>({
    route: Endpoints.GetTeam(pagination.page, pagination.limit).route,
    method: Endpoints.GetTeam(pagination.page, pagination.limit).method,
    body: {
      role: filterForm.values.role,
    },
  });
  useEffect(() => {
    reloadTeam();
  }, [filterForm.values.role]);

  interface AdminFormProps {
    id: string;
    name: string;
    email: string;
    role: string;
    phone: string;
    scopes: AdminScopeValueProps[];
  }

  const memberForm = useForm<AdminFormProps>({
    initialValues: {
      id: "",
      name: "",
      email: "",

      role: "admin",
      phone: "",
      scopes: [],
    },
    validate: {
      name: (value) => (value && value.length > 4 ? null : "Please enter name"),
      phone: (value) => (value && value.length === 11 ? null : "Invalid phone"),
      email: (value) =>
        validateEmail(value, true) ? null : "Enter a valid email",
    },
  });

  const [viewingMember, setViewingMember] = useState<AdminProfileProps>();

  const [memberModalAction, setMemberModalAction] = useState<
    "create" | "update" | "view"
  >("create");

  const [
    isMemberModalOpened,
    { open: OpenMemberModal, close: CloseMemberModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingProfile, setCreatingOrUpdatingProfile] =
    useState<boolean>(false);
  const CreateNewMember = async () => {
    const { hasErrors: formHasErrors } = memberForm.validate();
    // @ts-ignore
    const { name, role, phone, email, password, scopes } = memberForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const requestData: any = {
        name,
        role,
        password,
        email,
        phone,
        scopes,
      };

      const r = await PerformRequest({
        method: Endpoints.GetOnboardTeamMember().method,
        route: Endpoints.GetOnboardTeamMember().route,
        data: requestData,
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if (r.status === HttpErrorCodesObj.Forbidden) {
          showValidationErrors(r);
        }
        if (r.status === HttpErrorCodesObj.Conflict) {
          showResponseErrors(r);
        }
        if (r.status === HttpSuccessCodesObj.Created) {
          CancelCreateMember();
          reloadTeam();
          DefaultSuccessNotification("Profile created successfully!");
        }
      }
    }
  };
  const UpdateMember = async () => {
    const { hasErrors: formHasErrors } = memberForm.validate();
    // @ts-ignore
    const { id, name, role, phone, email, password, scopes } =
      memberForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const requestData: any = {
        name,
        role,
        email,
        phone,
      };

      // Include scopes only for admin role
      if (role === "admin") {
        requestData.scopes = scopes;
      }

      const r = await PerformRequest({
        method: Endpoints.GetUpdateTeamMemberProfile(id).method,
        route: Endpoints.GetUpdateTeamMemberProfile(id).route,
        data: requestData,
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if ([403, 409].includes(r.status)) {
          showResponseErrors(r);
        }
        if (r.status === 200) {
          CancelCreateMember();
          reloadTeam();
          DefaultSuccessNotification("Profile has been updated!");
        }
      }
    }
  };

  const [deletingMemberID, setDeletingMemberID] = useState<string>("");
  const DeleteMember = async (id: string) => {
    // @ts-ignore
    setDeletingMemberID(id);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteTeamMemberProfile(id).method,
      route: Endpoints.GetDeleteTeamMemberProfile(id).route,

      callback: () => {
        setCreatingOrUpdatingProfile(false);
        setDeletingMemberID("");
      },
    });
    setDeletingMemberID("");
    setCreatingOrUpdatingProfile(false);
    if (r) {
      if (r.status === 200) {
        CancelCreateMember();
        reloadTeam();
        DefaultSuccessNotification("Profile has been REMOVED!");
      }
    }
  };

  const CancelCreateMember = () => {
    memberForm.reset();
    setDeletingMemberID("");
    CloseMemberModal();
  };
  const memberFormInputProps = {
    my: 5,
  };

  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  return (
    <Container maxWidth="lg">
      {context && context.profile ? (
        <>
          <div className="team-container">
            <div className="component-header flex-row align-end justify-between">
              <Text className="label">Team Members</Text>
              <div className="flex-row align-center">
                <Button
                  color={Colors.PurplePrimary}
                  rightSection={<IconPlus size={16} />}
                  mr={10}
                  onClick={() => {
                    OpenMemberModal();
                    setMemberModalAction("create");
                    memberForm.reset();
                  }}
                  disabled={
                    isCreatingOrUpdatingProfile ||
                    isLoadingTeam ||
                    context.profile.role !== "superadmin"
                  }
                >
                  Add Profile
                </Button>
                <Button
                  color={Colors.PurplePrimary}
                  variant="outline"
                  onClick={reloadTeam}
                  loading={isLoadingTeam}
                  rightSection={<IconReload size={16} />}
                >
                  Reload
                </Button>
              </div>
            </div>
            <Divider w="100%" my={10} />
            <Select
              w={200}
              {...filterForm.getInputProps("role")}
              {...selectProps}
              label="Filter by role"
              data={[
                ...ExtendedAdminRolesList.map((role) => {
                  return {
                    value: role.value,
                    label: role.label,
                  };
                }),
                {
                  value: "all",
                  label: "All",
                },
              ]}
            />

            <div className="team">
              {isLoadingTeam ? (
                <MegaLoader
                  size={50}
                  containerStyle={{
                    marginTop: 45,
                    marginBottom: 45,
                  }}
                />
              ) : (
                <>
                  {!team?.data || team?.data.length === 0 ? (
                    <DisplayAlert
                      title="No Profiles Found"
                      children={<Text>No member profiles except you!</Text>}
                    />
                  ) : (
                    <DatagridDisplay
                      rows={team?.data.filter(
                        (t) => t.id !== context?.profile?.id
                      )}
                      isLoading={isLoadingTeam}
                      columns={[
                        {
                          field: "name",
                          flex: 1,
                          headerName: "Name",
                          renderCell: (
                            params: GridRenderCellParams<AdminProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text {...textProps}>{params.row.name}</Text>
                            </div>
                          ),
                        },
                        {
                          field: "email",
                          flex: 1,
                          headerName: "Email",
                          renderCell: (
                            params: GridRenderCellParams<AdminProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <CopyButton value={params.row.email}>
                                {({ copied, copy }) => (
                                  <Text
                                    fw={500}
                                    {...textProps}
                                    className="pointer"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                    }}
                                  >
                                    {params.row.email}
                                  </Text>
                                )}
                              </CopyButton>
                            </div>
                          ),
                        },
                        {
                          field: "phone",
                          flex: 1,
                          headerName: "Phone",
                          renderCell: (
                            params: GridRenderCellParams<AdminProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <CopyButton value={params.row.phone}>
                                {({ copied, copy }) => (
                                  <Text
                                    fw={500}
                                    {...textProps}
                                    className="pointer"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                    }}
                                  >
                                    {params.row.phone}
                                  </Text>
                                )}
                              </CopyButton>
                            </div>
                          ),
                        },
                        {
                          field: "role",
                          flex: 1,
                          headerName: "Role",
                          renderCell: (
                            params: GridRenderCellParams<AdminProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <Text {...textProps} tt="capitalize">
                                {params.row.role}
                              </Text>
                            </div>
                          ),
                        },

                        {
                          headerName: "Actions",
                          field: "id",
                          flex: 1,
                          renderCell: (
                            params: GridRenderCellParams<AdminProfileProps>
                          ) => (
                            <div className="flex-row flex-1 align-center">
                              <div className="flex-row align-center">
                                <ActionIcon
                                  disabled={
                                    context?.profile?.role !== "superadmin"
                                  }
                                  variant="subtle"
                                  onClick={() => {
                                    OpenMemberModal();
                                    setMemberModalAction("update");
                                    // @ts-ignore
                                    memberForm.setValues({
                                      id: params.row.id,
                                      name: params.row.name,
                                      role: params.row.role,
                                      phone: params.row.phone,
                                      email: params.row.email,
                                      scopes: params.row.scopes || [], // Use actions property from AdminProfileProps
                                    });
                                  }}
                                >
                                  <IconEdit size={18} />
                                </ActionIcon>
                                &nbsp; &nbsp;
                                <ActionIcon
                                  disabled={
                                    context?.profile?.role !== "superadmin"
                                  }
                                  variant="subtle"
                                  onClick={() => {
                                    OpenMemberModal();
                                    setMemberModalAction("view");

                                    setViewingMember(params.row);
                                  }}
                                >
                                  <IconEye size={18} />
                                </ActionIcon>
                                &nbsp; &nbsp;
                                <Popover
                                  width={200}
                                  position="bottom"
                                  withArrow
                                  shadow="md"
                                >
                                  <Popover.Target>
                                    <ActionIcon
                                      disabled={
                                        context?.profile?.role !== "superadmin"
                                      }
                                      variant="subtle"
                                      color={Colors.RedPrimary}
                                    >
                                      <IconTrash size={18} />
                                    </ActionIcon>
                                  </Popover.Target>
                                  <Popover.Dropdown>
                                    <Text size="xs" c={Colors.RedPrimary}>
                                      This action CANNOT be undone!
                                    </Text>
                                    <Button
                                      color={Colors.RedPrimary}
                                      mt={5}
                                      w="100%"
                                      onClick={() => {
                                        DeleteMember(params.row.id);
                                      }}
                                      loading={
                                        deletingMemberID === params.row.id
                                      }
                                      disabled={
                                        deletingMemberID.length > 0 &&
                                        deletingMemberID !== params.row.id
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </Popover.Dropdown>
                                </Popover>
                              </div>
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <Modal
            opened={isMemberModalOpened}
            onClose={CancelCreateMember}
            centered
            size="lg"
            withCloseButton={false}
          >
            <div className="team-modal flex-col">
              <div className="flex-row align-center width-100 justify-between">
                <div className="flex-row align-center">
                  <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                    {memberModalAction} Profile
                  </Text>
                </div>
                <ActionIcon
                  variant="outline"
                  color={Colors.BluePrimary}
                  onClick={CancelCreateMember}
                >
                  <IconX color={Colors.BluePrimary} size={18} />
                </ActionIcon>
              </div>
              {memberModalAction === "view" ? (
                <>
                  {viewingMember && (
                    <>
                      <Table
                        mt={20}
                        variant="vertical"
                        layout="fixed"
                        withTableBorder
                      >
                        <Table.Thead>
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th w={200}>Email</Table.Th>
                            <Table.Td>
                              <CopyButton value={viewingMember.email}>
                                {({ copied, copy }) => (
                                  <Tooltip
                                    label={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <div
                                      className="pointer flex-row align-center"
                                      onClick={copy}
                                      style={{
                                        color: copied
                                          ? Colors.PurplePrimary
                                          : Colors.BluePrimary,
                                        height: "100%",
                                      }}
                                    >
                                      {getEllipsisFromString(
                                        viewingMember.email,
                                        20
                                      )}
                                    </div>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </Table.Td>
                          </Table.Tr>
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th w={200}>Phone</Table.Th>
                            <Table.Td>
                              <CopyButton value={viewingMember.phone}>
                                {({ copied, copy }) => (
                                  <Tooltip
                                    label={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <div
                                      className="pointer flex-row align-center"
                                      onClick={copy}
                                      style={{
                                        color: copied
                                          ? Colors.PurplePrimary
                                          : Colors.BluePrimary,
                                        height: "100%",
                                      }}
                                    >
                                      {getEllipsisFromString(
                                        viewingMember.phone,
                                        14
                                      )}
                                    </div>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </Table.Td>
                          </Table.Tr>

                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Name</Table.Th>
                            <Table.Td>{viewingMember.name}</Table.Td>
                          </Table.Tr>
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Role</Table.Th>
                            <Table.Td tt="capitalize">
                              {viewingMember.role}
                            </Table.Td>
                          </Table.Tr>

                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Creation Date</Table.Th>
                            <Table.Td>
                              {dayjs(viewingMember.createdAt).format(
                                "ddd, MMMM DD YYYY"
                              )}
                            </Table.Td>
                          </Table.Tr>
                        </Table.Thead>
                      </Table>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="flex-row align-center justify-between width-100">
                    <TextInput
                      {...memberForm.getInputProps("email")}
                      {...memberFormInputProps}
                      label="Email"
                      placeholder={`you@${AppDomain}`}
                      spellCheck={false}
                      w="48%"
                      leftSection={<IconAt size={16} />}
                    />
                    <TextInput
                      {...memberForm.getInputProps("phone")}
                      {...memberFormInputProps}
                      w="48%"
                      label="Phone"
                      placeholder="0802******"
                      spellCheck={false}
                      leftSection={<IconPhone size={16} />}
                    />
                  </div>
                  <TextInput
                    {...memberForm.getInputProps("name")}
                    {...memberFormInputProps}
                    w="100%"
                    label="Name"
                    placeholder="Full Name"
                    spellCheck={false}
                    leftSection={<IconUser size={16} />}
                  />

                  <Select
                    leftSection={<IconKey size={16} />}
                    label="Role"
                    {...selectProps}
                    {...memberForm.getInputProps("role")}
                    {...memberFormInputProps}
                    w="100%"
                    styles={{
                      option: { textTransform: "capitalize" },
                      input: { textTransform: "capitalize" },
                    }}
                    data={ExtendedAdminRolesList.map((role) => {
                      return {
                        value: role.value,
                        label: role.label,
                      };
                    })}
                  />

                  {/* Scope Selection for Admin */}
                  {memberForm.values.role === "admin" && (
                    <Card
                      withBorder
                      radius="md"
                      mt={10}
                      style={{
                        background:
                          "linear-gradient(135deg, #f8f9ff 0%, #e7f5ff 100%)",
                        border: "1px solid #339af0",
                      }}
                    >
                      <Stack gap="md">
                        <Group gap={8} align="center">
                          <ThemeIcon
                            size="sm"
                            color="blue"
                            variant="light"
                            radius="xl"
                          >
                            <IconShield size={14} />
                          </ThemeIcon>
                          <Text fw={600} size="md" c="blue">
                            Admin Permissions
                          </Text>
                          <Badge variant="light" color="blue" size="sm">
                            Select Scopes
                          </Badge>
                        </Group>

                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
                          Choose which areas this admin can access and manage.
                          You can select multiple permissions.
                        </Text>

                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                          {scopeOptions.map((scope) => {
                            const Icon = scope.icon;
                            const isSelected =
                              memberForm.values.scopes.includes(scope.value);

                            return (
                              <Paper
                                key={scope.value}
                                p="md"
                                radius="md"
                                style={{
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  background: isSelected
                                    ? `linear-gradient(135deg, rgba(${
                                        scope.color === "blue"
                                          ? "51, 154, 240"
                                          : scope.color === "green"
                                          ? "34, 197, 94"
                                          : scope.color === "violet"
                                          ? "139, 92, 246"
                                          : scope.color === "orange"
                                          ? "251, 146, 60"
                                          : "20, 184, 166"
                                      }, 0.1) 0%, rgba(255,255,255,0.9) 100%)`
                                    : "rgba(255,255,255,0.7)",
                                  border: isSelected
                                    ? `1px solid ${
                                        scope.color === "blue"
                                          ? "#339af0"
                                          : scope.color === "green"
                                          ? "#22c55e"
                                          : scope.color === "violet"
                                          ? "#8b5cf6"
                                          : scope.color === "orange"
                                          ? "#fb923c"
                                          : "#14b8a6"
                                      }`
                                    : "1px solid #e9ecef",
                                }}
                                onClick={() => {
                                  const currentScopes =
                                    memberForm.values.scopes;
                                  const newScopes = isSelected
                                    ? currentScopes.filter(
                                        (s) => s !== scope.value
                                      )
                                    : [...currentScopes, scope.value];
                                  memberForm.setFieldValue("scopes", newScopes);
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.transform =
                                      "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 12px rgba(0,0,0,0.1)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.transform =
                                      "translateY(0)";
                                    e.currentTarget.style.boxShadow = "";
                                  }
                                }}
                              >
                                <Group gap="md" align="flex-start">
                                  <div style={{ position: "relative" }}>
                                    <ThemeIcon
                                      size="lg"
                                      color={scope.color}
                                      variant={isSelected ? "filled" : "light"}
                                      radius="md"
                                    >
                                      <Icon size={20} />
                                    </ThemeIcon>
                                  </div>

                                  <div style={{ flex: 1 }}>
                                    <Group gap={8} mb={4}>
                                      <Text
                                        fw={600}
                                        size="sm"
                                        c={isSelected ? scope.color : "dark"}
                                      >
                                        {scope.label}
                                      </Text>
                                      {isSelected && (
                                        <Badge
                                          size="xs"
                                          color="green"
                                          variant="filled"
                                        >
                                          Selected
                                        </Badge>
                                      )}
                                    </Group>
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      style={{ lineHeight: 1.4 }}
                                    >
                                      {scope.description}
                                    </Text>
                                  </div>

                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => {}} // Handled by paper click
                                    color={scope.color}
                                    size="sm"
                                    style={{ pointerEvents: "none" }}
                                  />
                                </Group>
                              </Paper>
                            );
                          })}
                        </SimpleGrid>

                        {memberForm.values.scopes.length > 0 && (
                          <Paper
                            p="sm"
                            radius="md"
                            style={{
                              background: "rgba(34, 197, 94, 0.1)",
                              border: "1px solid #22c55e",
                            }}
                          >
                            <Group gap={8} align="center">
                              <ThemeIcon
                                size="sm"
                                color="green"
                                variant="light"
                              >
                                <IconCheck size={12} />
                              </ThemeIcon>
                              <Text size="sm" fw={500} c="green">
                                {memberForm.values.scopes.length} permission
                                {memberForm.values.scopes.length > 1
                                  ? "s"
                                  : ""}{" "}
                                selected
                              </Text>
                            </Group>
                          </Paper>
                        )}
                      </Stack>
                    </Card>
                  )}

                  {/* Super Admin Banner */}
                  {memberForm.values.role === "superadmin" && (
                    <Card
                      withBorder
                      radius="md"
                      mt={10}
                      style={{
                        background:
                          "linear-gradient(135deg, #fdf4ff 0%, #f3e8ff 100%)",
                        border: "1px solid #a855f7",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {/* Decorative background pattern */}
                      <div
                        style={{
                          position: "absolute",
                          top: -20,
                          right: -20,
                          width: 80,
                          height: 80,
                          background: "rgba(168, 85, 247, 0.1)",
                          borderRadius: "50%",
                          zIndex: 0,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: -30,
                          left: -30,
                          width: 100,
                          height: 100,
                          background: "rgba(168, 85, 247, 0.05)",
                          borderRadius: "50%",
                          zIndex: 0,
                        }}
                      />

                      <div style={{ position: "relative", zIndex: 1 }}>
                        <Group gap="md" align="center">
                          <div style={{ position: "relative" }}>
                            <ThemeIcon
                              size="xl"
                              color="violet"
                              variant="gradient"
                              gradient={{
                                from: "violet",
                                to: "purple",
                                deg: 45,
                              }}
                              radius="md"
                              style={{
                                boxShadow: "0 4px 12px rgba(168, 85, 247, 0.3)",
                              }}
                            >
                              <IconUserCheck size={24} />
                            </ThemeIcon>
                            {/* Crown/Star indicator */}
                            <div
                              style={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                background:
                                  "linear-gradient(45deg, #fbbf24, #f59e0b)",
                                borderRadius: "50%",
                                width: 18,
                                height: 18,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 6px rgba(251, 191, 36, 0.4)",
                              }}
                            >
                              <Text size="xs" c="white" fw={700}>
                                ★
                              </Text>
                            </div>
                          </div>

                          <div style={{ flex: 1 }}>
                            <Group gap={8} align="center" mb={4}>
                              <Text fw={700} size="lg" c="violet">
                                Super Administrator
                              </Text>
                              <Badge
                                variant="gradient"
                                gradient={{
                                  from: "violet",
                                  to: "purple",
                                  deg: 45,
                                }}
                                size="sm"
                              >
                                Full Access
                              </Badge>
                            </Group>
                            <Text
                              size="sm"
                              c="dimmed"
                              style={{ lineHeight: 1.5 }}
                            >
                              This user will have{" "}
                              <Text component="span" fw={600} c="violet">
                                complete access
                              </Text>{" "}
                              to all features, settings, and administrative
                              functions on the platform.
                            </Text>
                          </div>
                        </Group>

                        <Paper
                          p="md"
                          radius="md"
                          mt="md"
                          style={{
                            background: "rgba(255, 255, 255, 0.7)",
                            border: "1px solid rgba(168, 85, 247, 0.2)",
                          }}
                        >
                          <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="xs">
                            {scopeOptions.map((scope) => {
                              const Icon = scope.icon;
                              return (
                                <Group key={scope.value} gap={6} align="center">
                                  <ThemeIcon
                                    size="xs"
                                    color={scope.color}
                                    variant="light"
                                  >
                                    <Icon size={10} />
                                  </ThemeIcon>
                                  <Text size="xs" fw={500} c="dark">
                                    {scope.label.replace(" Management", "")}
                                  </Text>
                                </Group>
                              );
                            })}
                          </SimpleGrid>
                        </Paper>

                        <Group gap={8} align="center" mt="sm" justify="center">
                          <ThemeIcon size="xs" color="green" variant="light">
                            <IconShield size={10} />
                          </ThemeIcon>
                          <Text size="xs" fw={600} c="violet" ta="center">
                            No permission restrictions • Full system control
                          </Text>
                        </Group>
                      </div>
                    </Card>
                  )}
                  <div className="flex-row width-100 justify-between align-center">
                    <Button
                      mt={10}
                      w="48%"
                      color={Colors.PurplePrimary}
                      onClick={
                        memberModalAction === "create"
                          ? CreateNewMember
                          : UpdateMember
                      }
                      loading={isCreatingOrUpdatingProfile}
                      tt="capitalize"
                    >
                      {memberModalAction}
                    </Button>
                    <Button
                      mt={10}
                      w="48%"
                      variant="outline"
                      onClick={CancelCreateMember}
                      disabled={isCreatingOrUpdatingProfile}
                      color={Colors.RedPrimary}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Modal>
        </>
      ) : (
        <MegaLoader />
      )}
    </Container>
  );
};

export default Team;
